import * as React from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "@/components/Icon";
import { sizeStyles } from "../constants";
import { useHierarchyContext } from "../context";
import type { HierarchyNode, NodeState } from "../types";

interface NodeItemProps<T extends HierarchyNode> {
  node: T;
  renderNode?: (node: T, state: NodeState) => React.ReactNode;
}

export function NodeItem<T extends HierarchyNode>({ node, renderNode }: NodeItemProps<T>) {
  const {
    selectedId,
    selectNode,
    navigateToNode,
    variant,
    size,
    showBullets,
    rootNodeId,
    stickyRootNode,
    navigationStack,
    setRootNodeSticky,
    useSplitLayout,
  } = useHierarchyContext<T>();

  const nodeRef = React.useRef<HTMLDivElement>(null);
  const originalOffsetTopRef = React.useRef<number | null>(null);

  const isSelected = selectedId === node.id;
  const isDisabled = node.disabled || false;
  const hasChildren = Boolean(node.children && node.children.length > 0);
  const hasMetadata = Boolean(node.metadata && Object.keys(node.metadata).length > 0);
  const canDrillDown = hasChildren || hasMetadata;
  const isRootNode =
    rootNodeId !== undefined && node.id !== undefined && rootNodeId === node.id && navigationStack.length === 0;

  // Capture the original offset on mount (before any sticky positioning is applied)
  React.useLayoutEffect(() => {
    if (isRootNode && stickyRootNode && nodeRef.current && originalOffsetTopRef.current === null) {
      requestAnimationFrame(() => {
        if (nodeRef.current) {
          originalOffsetTopRef.current = nodeRef.current.offsetTop;
        }
      });
    }
  }, [isRootNode, stickyRootNode]);

  // Handle bidirectional sticky positioning
  React.useEffect(() => {
    if (!isRootNode || !stickyRootNode || !nodeRef.current) return;

    // Find the actual scrollable container (the one with overflow-auto)
    let scrollContainer = nodeRef.current.parentElement;
    while (scrollContainer) {
      const overflowY = window.getComputedStyle(scrollContainer).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        break;
      }
      scrollContainer = scrollContainer.parentElement;
    }

    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!nodeRef.current || originalOffsetTopRef.current === null) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const containerScrollTop = scrollContainer.scrollTop;
      const containerHeight = containerRect.height;
      const nodeHeight = nodeRef.current.offsetHeight;
      const originalOffsetTop = originalOffsetTopRef.current;

      const nodeTopRelativeToViewport = originalOffsetTop - containerScrollTop;
      const nodeBottomRelativeToViewport = nodeTopRelativeToViewport + nodeHeight;

      const isAboveViewport = nodeTopRelativeToViewport < 0;
      const isBelowViewport = nodeBottomRelativeToViewport > containerHeight;

      if (isAboveViewport) {
        setRootNodeSticky("top", node);
      } else if (isBelowViewport) {
        setRootNodeSticky("bottom", node);
      } else {
        setRootNodeSticky(null, null);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [isRootNode, stickyRootNode, node, setRootNodeSticky]);

  const handleClick = () => {
    if (isDisabled) return;

    if (useSplitLayout) {
      selectNode(node);
    } else if (canDrillDown) {
      navigateToNode(node);
    } else {
      selectNode(node);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const state: NodeState = {
    isSelected,
    isDisabled,
    hasChildren,
    hasMetadata,
    level: 0, // Flat in drill-down mode
  };

  const containerStyles = twMerge("relative cursor-pointer group", isDisabled && "opacity-50 cursor-not-allowed");

  const backgroundStyles = twMerge(
    "absolute inset-0 transition-colors",
    // Root node background
    isRootNode && !isSelected && !isDisabled && "group-hover:bg-background-hover",
    isRootNode &&
      isSelected &&
      !isDisabled &&
      variant === "subtle" &&
      "bg-brand-primary/10 group-hover:bg-brand-primary/15",
    isRootNode && isSelected && isDisabled && variant === "subtle" && "bg-brand-primary/10",
    isRootNode &&
      isSelected &&
      !isDisabled &&
      variant === "solid" &&
      "bg-brand-primary group-hover:bg-brand-primary/90",
    isRootNode && isSelected && isDisabled && variant === "solid" && "bg-brand-primary",
    // Regular node backgrounds
    !isRootNode && variant === "subtle" && !isSelected && !isDisabled && "group-hover:bg-background-hover",
    !isRootNode &&
      variant === "subtle" &&
      isSelected &&
      !isDisabled &&
      "bg-brand-primary/10 group-hover:bg-brand-primary/15",
    !isRootNode && variant === "subtle" && isSelected && isDisabled && "bg-brand-primary/10",
    !isRootNode && variant === "solid" && !isSelected && !isDisabled && "group-hover:bg-background-hover",
    !isRootNode &&
      variant === "solid" &&
      isSelected &&
      !isDisabled &&
      "bg-brand-primary group-hover:bg-brand-primary/90",
    !isRootNode && variant === "solid" && isSelected && isDisabled && "bg-brand-primary"
  );

  const contentStyles = twMerge(
    "relative flex items-center gap-2 text-text-body hover:text-text-heading transition-colors",
    sizeStyles[size].node,
    isRootNode && !isSelected && "font-semibold",
    variant === "subtle" && isSelected && "text-brand-primary",
    variant === "solid" && isSelected && "text-white"
  );

  return (
    <div
      ref={nodeRef}
      className={containerStyles}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="treeitem"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      tabIndex={0}
      data-node-id={node.id}
    >
      {/* Background layer */}
      <div className={backgroundStyles} />

      {/* Content layer */}
      <div className={contentStyles}>
        {/* Bullet */}
        {showBullets && (
          <div
            className={twMerge(
              "relative rounded-full flex items-center justify-center flex-shrink-0 z-20",
              isRootNode && "bg-brand-primary border-brand-primary",
              !isRootNode && "bg-background-muted border border-border-default"
            )}
            style={{
              width: sizeStyles[size].bullet,
              height: sizeStyles[size].bullet,
            }}
          >
            {node.icon && (
              <Icon
                name={node.icon as Parameters<typeof Icon>[0]["name"]}
                size={sizeStyles[size].icon}
                weight={isRootNode ? "fill" : "regular"}
                className={isRootNode ? "text-white" : "text-text-body"}
              />
            )}
          </div>
        )}

        {/* Node Content */}
        <div className="flex-1 min-w-0">
          {renderNode ? renderNode(node, state) : <span className="truncate">{node.name}</span>}
        </div>

        {/* Chevron (single-pane mode only) */}
        {canDrillDown && !useSplitLayout && (
          <div className="flex-shrink-0">
            <Icon name="CaretRight" size={sizeStyles[size].icon} className="text-text-muted" />
          </div>
        )}
      </div>
    </div>
  );
}
